/**
 * AdminOrgsPage — platform-wide org browser. Only renders for users with
 * platform_admin access. Lists every org with summary stats, click-through
 * to detail page.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Shield, Search, Building2, Users, MapPin, ChevronRight, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useAdmin } from '../context/AdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function relativeTime(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

export default function AdminOrgsPage() {
  const { token } = useAuth();
  const { isPlatformAdmin, loading: adminLoading } = useAdmin();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!token || !isPlatformAdmin) return;
    const controller = new AbortController();
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    axios
      .get(`${API}/admin/orgs?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      })
      .then((res) => {
        setOrgs(res.data.orgs || []);
        setTotal(res.data.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [token, isPlatformAdmin, search]);

  if (adminLoading) return null;

  if (!isPlatformAdmin) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <Card className="max-w-md w-full p-8 text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
          <h2 className="text-xl font-semibold mb-2">Access denied</h2>
          <p className="text-sm text-muted-foreground">
            This area is for Tillytics platform administrators only.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-orgs-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground mb-1">
            <Shield className="w-3.5 h-3.5" />
            Platform Admin
          </div>
          <h1 className="text-3xl font-semibold">Organisations</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every account on Tillytics. {total} {total === 1 ? 'org' : 'orgs'} total.
          </p>
        </div>
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search orgs by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="admin-orgs-search"
          />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-muted-foreground text-sm">Loading orgs…</div>
          ) : orgs.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground text-sm">No organisations match your search.</div>
          ) : (
            <div className="divide-y">
              {orgs.map((o) => (
                <button
                  key={o.id}
                  className="w-full text-left p-4 hover:bg-muted/40 transition-colors flex items-center gap-4"
                  onClick={() => navigate(`/admin/orgs/${o.id}`)}
                  data-testid={`admin-org-row-${o.id}`}
                >
                  <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{o.name || 'Unnamed org'}</span>
                      {o.owner_email && (
                        <span className="text-xs text-muted-foreground">· {o.owner_email}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                      <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {o.user_count}</span>
                      <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {o.location_count}</span>
                      <span>·</span>
                      <span>{o.enabled_modules_count} modules</span>
                      <span>·</span>
                      <span>{o.recent_uploads_7d} uploads (7d)</span>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground text-right">
                    <div>Last activity</div>
                    <div className="font-medium text-foreground mt-0.5">{relativeTime(o.last_activity)}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
